import React from 'react';

/* ─── shared input style ─── */
const inputStyle = {
  width: '100%', padding: '10px 14px', borderRadius: 6,
  border: '0.762px solid rgba(42,42,42,0.2)', background: '#FAF7F2',
  fontFamily: 'Inter', fontSize: 13, color: '#191919', outline: 'none',
  transition: 'border-color 0.18s', boxSizing: 'border-box',
};

const focusHandler = (e) => (e.currentTarget.style.borderColor = 'rgba(42,42,42,0.5)');
const blurHandler  = (e) => (e.currentTarget.style.borderColor = 'rgba(42,42,42,0.2)');

/* ─── FieldGroup ─── */
export const FieldGroup = ({ label, required, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
    <label style={{ fontFamily: 'Inter', fontSize: 12, fontWeight: 600, color: '#191919' }}>
      {label}{required && <span style={{ color: '#a33', marginLeft: 2 }}>*</span>}
    </label>
    {children}
  </div>
);

/* ─── TextInput ─── */
export const TextInput = ({ value, onChange, placeholder, type = 'text', required }) => (
  <input
    type={type}
    value={value ?? ''}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    required={required}
    style={inputStyle}
    onFocus={focusHandler}
    onBlur={blurHandler}
  />
);

/* ─── SelectInput ─── */
// FIX: Always render the placeholder option (disabled + hidden) so the select
// shows a prompt when empty, but doesn't break required validation or edit mode.
export const SelectInput = ({ value, onChange, options, required }) => (
  <select
    value={value ?? ''}
    onChange={(e) => onChange(e.target.value)}
    required={required}
    style={{ ...inputStyle, cursor: 'pointer' }}
    onFocus={focusHandler}
    onBlur={blurHandler}
  >
    <option value="" disabled hidden>— Select —</option>
    {options.map((o) =>
      typeof o === 'string'
        ? <option key={o} value={o}>{o}</option>
        : <option key={o.value} value={o.value}>{o.label}</option>
    )}
  </select>
);

/* ─── TextArea ─── */
export const TextArea = ({ value, onChange, placeholder, rows = 3 }) => (
  <textarea
    value={value ?? ''}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    rows={rows}
    style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }}
    onFocus={focusHandler}
    onBlur={blurHandler}
  />
);

/* ─── FormGrid ─── */
export const FormGrid = ({ cols = 2, children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 14 }}>
    {children}
  </div>
);

/* ─── FormActions ─── */
export const FormActions = ({ onCancel, submitLabel = 'Save', loading }) => (
  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
    <button
      type="button"
      onClick={onCancel}
      style={{
        background: 'none', border: '0.762px solid rgba(42,42,42,0.25)',
        borderRadius: 6, padding: '9px 22px', fontFamily: 'Inter',
        fontSize: 13, cursor: 'pointer', color: '#191919',
        transition: 'background 0.15s',
      }}
      onMouseEnter={e => e.currentTarget.style.background = 'rgba(214,184,147,0.3)'}
      onMouseLeave={e => e.currentTarget.style.background = 'none'}
    >Cancel</button>
    <button
      type="submit"
      disabled={loading}
      style={{
        background: loading ? '#555' : '#0D0D0D', color: '#D6B893', border: 'none',
        borderRadius: 6, padding: '9px 22px', fontFamily: 'Inter',
        fontSize: 13, fontWeight: 500, cursor: loading ? 'not-allowed' : 'pointer',
        transition: 'background 0.2s',
      }}
      onMouseEnter={e => { if (!loading) e.currentTarget.style.background = '#1a1a1a'; }}
      onMouseLeave={e => { if (!loading) e.currentTarget.style.background = loading ? '#555' : '#0D0D0D'; }}
    >{loading ? 'Saving…' : submitLabel}</button>
  </div>
);

/* ─── DeleteConfirm ─── */
export const DeleteConfirm = ({ label, onConfirm, onCancel, loading }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
    <p style={{ fontFamily: 'Inter', fontSize: 14, color: '#191919', lineHeight: 1.6 }}>
      Are you sure you want to delete <strong>{label}</strong>? This action cannot be undone.
    </p>
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
      <button
        type="button"
        onClick={onCancel}
        style={{
          background: 'none', border: '0.762px solid rgba(42,42,42,0.25)',
          borderRadius: 6, padding: '9px 22px', fontFamily: 'Inter',
          fontSize: 13, cursor: 'pointer', color: '#191919',
        }}
      >Cancel</button>
      <button
        type="button"
        onClick={onConfirm}
        disabled={loading}
        style={{
          background: loading ? '#888' : '#a33', color: '#fff', border: 'none',
          borderRadius: 6, padding: '9px 22px', fontFamily: 'Inter',
          fontSize: 13, fontWeight: 500, cursor: loading ? 'not-allowed' : 'pointer',
        }}
      >{loading ? 'Deleting…' : 'Delete'}</button>
    </div>
  </div>
);